import { type ReactNode, useId, useMemo } from 'react';

interface ScopedStyleContainerProps {
  css: string;
  children: ReactNode;
}

/** Scopes arbitrary CSS by prefixing every selector with a unique container id, so Grapesjs pallet-tag template CSS cannot leak into the app — while keeping content in the regular DOM so html2canvas and react-to-print still work. */
export function ScopedStyleContainer({ css, children }: ScopedStyleContainerProps) {
  const id = useId();
  const containerId = `pt-${id.replace(/:/g, '')}`;

  const scopedCss = useMemo(() => scopeCss(css, `#${containerId}`), [css, containerId]);

  return (
    <div id={containerId}>
      <style>{scopedCss}</style>
      {children}
    </div>
  );
}

/**
 * Prefixes every CSS selector in a stylesheet with a scope selector.
 * Handles grouped selectors, @media blocks, and @keyframes.
 */
function scopeCss(css: string, scope: string): string {
  if (!css) return '';

  return css.replace(
    // Match either an @-rule block or a normal rule's selector portion
    /(@(?:media|supports|container)[^{]*\{)|(@(?:keyframes|font-face)[^{]*\{)|([^{}@]+)(\{)/g,
    (match, mediaRule, passthroughRule, selectors, brace) => {
      // @media, @supports, @container — leave the @-rule intact, contents will be scoped by recursion
      if (mediaRule) return mediaRule;

      // @keyframes, @font-face — pass through completely
      if (passthroughRule) return passthroughRule;

      if (selectors && brace) {
        const scoped = selectors
          .split(',')
          .map((s: string) => {
            const trimmed = s.trim();
            if (!trimmed) return s;

            // Don't double-scope if already scoped, and skip @-rules
            if (trimmed.startsWith(scope) || trimmed.startsWith('@')) return s;

            // `body` / `html` / `:root` selectors — replace with the scope
            if (/^(body|html|:root)(\s|$|[.#[:>+~])/.test(trimmed)) {
              return s.replace(/^(\s*)(body|html|:root)/, `$1${scope}`);
            }

            // Prefix everything else
            return s.replace(trimmed, `${scope} ${trimmed}`);
          })
          .join(',');

        return scoped + brace;
      }

      return match;
    }
  );
}
